import { IconAlertTriangle, IconPlugConnectedX, IconLock, IconRoute } from "@tabler/icons-react";
import { useT } from "../i18n/provider";
import type { Failure } from "../lib/api-result";
import { Button, Empty } from "./kit";

/**
 * The honest failure block.
 *
 * §11.1's rule for a drill panel applies to every read here: **an error, a
 * pending read and a missing record are three DIFFERENT SENTENCES.** What this
 * component adds is the fourth: `not_available` — the endpoint is not on this api
 * build. A console that drew «no rows» for a route that does not exist would be
 * telling an operator that a queue is empty when nothing has ever asked.
 */
export function FailureBlock({ failure, onRetry }: { failure: Failure; onRetry?: () => void }) {
  const { i18n } = useT();

  if (failure.kind === "not_available") {
    return (
      <Empty
        icon={IconRoute}
        title={i18n._("Not available on this api build yet")}
        description={i18n._(
          "The console asked for an endpoint the running api does not serve. This screen will work as soon as that module is registered; nothing here is broken.",
        )}
      />
    );
  }

  if (failure.kind === "unreachable") {
    return (
      <Empty
        icon={IconPlugConnectedX}
        title={i18n._("The api did not answer")}
        description={i18n._("Nothing was read, so nothing on this screen is out of date — it is simply absent.")}
        action={onRetry ? <Button onClick={onRetry}>{i18n._("Try again")}</Button> : undefined}
      />
    );
  }

  if (failure.kind === "forbidden") {
    return (
      <Empty
        icon={IconLock}
        title={i18n._("You may not read this")}
        // The api's own `detail` is the useful half: «this payout is held pending
        // KYB re-verification» is the answer, and a generic «forbidden» throws it away.
        description={failure.detail || i18n._("Your role does not carry the permission this screen needs.")}
      />
    );
  }

  return (
    <Empty
      icon={IconAlertTriangle}
      title={failure.detail || i18n._("The api refused this request")}
      // The stable code, because it is what an operator quotes in a ticket and
      // what a developer greps for.
      description={failure.code}
      action={onRetry ? <Button onClick={onRetry}>{i18n._("Try again")}</Button> : undefined}
    />
  );
}

/**
 * A one-line version for a card on a dashboard, where a full block would be four
 * cards tall.
 */
export function FailureLine({ failure }: { failure: Failure }) {
  const { i18n } = useT();
  const message =
    failure.kind === "not_available"
      ? i18n._("Not available on this api build yet")
      : failure.kind === "unreachable"
        ? i18n._("The api did not answer")
        : failure.detail || failure.code;
  return (
    <p className="flex items-center gap-1.5 text-xs text-caution">
      <IconAlertTriangle size={14} stroke={1.5} aria-hidden />
      {message}
    </p>
  );
}
